import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Select from "react-select";
import countryList from "react-select-country-list";
import ReactCountryFlag from "react-country-flag";
import { useMemo } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  Container,
} from "@mui/material";
import imgUrl from "../assets/images/imgUrl";
import { apiServices } from "../services/apiServices";

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    country: "",
    countryCode: "+1",
    mobile: "",
    designation: "",
    email: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [errors, setErrors] = useState({});
  const countries = [
  { id: 1, name: "United States", code: "+1" },
  { id: 2, name: "India", code: "+91" },
  { id: 3, name: "United Kingdom", code: "+44" },
  { id: 4, name: "Australia", code: "+61" },
  { id: 5, name: "Canada", code: "+1" },
];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "Required";
    if (!formData.email.trim()) newErrors.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email";
    if (!formData.message.trim()) newErrors.message = "Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
 
  const countryOptions = useMemo(() => {
  return countryList().getData().map((country) => ({
    value: country.label,
    label: country.label,
    code: country.value, // ISO code (IN, US)
  }));
}, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await apiServices.submitContactForm(formData);
      if (response.status === 200 || response.status === 202) {
        setAlert({
          show: true,
          type: "success",
          message: "Submitted successfully!",
        });
        setFormData({ ...formData, firstName: "", lastName: "", mobile: "", message: "" });
      }
    } catch (error) {
      setAlert({ show: true, type: "error", message: "Failed to submit." });
    } finally {
      setLoading(false);
    }
  };

  // Modern pill-shaped input style matching the image
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "12px",
      "& fieldset": { borderColor: "#E0E0E0" },
      "&:hover fieldset": { borderColor: "#A3D139" },
      "&.Mui-focused fieldset": { borderColor: "#A3D139" },
    },
    mb: 2,
  };

  return (
    <Box sx={{ bgcolor: "#F4F9F4", pb: 10 }}>
      {/* 1. BANNER SECTION (Preserved) */}
      <Box
        className="contactSectionBg commonBannerBg"
        sx={{ backgroundSize: "cover", backgroundPosition: "center", height: "600px", position: "relative" }}
      >
       
      </Box>

      {/* 2. SPLIT LAYOUT SECTION (Like the image) */}
      <Container maxWidth="lg" sx={{ mt: -10, }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "40px",
            overflow: "hidden",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            minHeight: "500px",
          }}
        >
          {/* LEFT SIDE: Image/Character Area */}
          <Grid
            item
            md={6}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              position: "relative",
            }}
          >
            {/*character image URL */}
            <Box
              component="img"
              src={imgUrl.leftsideImg}
              sx={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Grid>

          {/* RIGHT SIDE: White Form Card */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              bgcolor: "white",
              p: { xs: 4, md: 8 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              borderRadius: { xs: "40px", md: "0 40px 40px 0" },
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box component="img" src={imgUrl.logo} sx={{ height: 30, mb: 2 }} />
              <Typography variant="h1" sx={{ fontWeight: 800, color: "#2D3436" }}>
                Get in touch
              </Typography>
            </Box>

            {alert.show && <Alert severity={alert.type} sx={{ mb: 2 }}>{alert.message}</Alert>}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    variant="outlined"
                    sx={inputSx}
                    value={formData.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    error={!!errors.firstName}
                  />
                </Grid>
            <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    variant="outlined"
                    sx={inputSx}
                    value={formData.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} >
                  <TextField
                    fullWidth
                    label="Email Address"
                    sx={{ ...inputSx, width: "28.5rem" }}
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    error={!!errors.email}
                  />
                </Grid>

                {/* Mobile with Country Code Dropdown */}
           <Grid item xs={12}>
            
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <Select
        options={countryOptions}
        placeholder="Select Country"
        value={countryOptions.find(
          (option) => option.value === formData.country
        )}
        onChange={(selected) =>
          handleInputChange("country", selected.value)
        }
        formatOptionLabel={(option) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ReactCountryFlag
              countryCode={option.code}
              svg
              style={{ width: "20px", height: "20px" }}
            />
            {option.label}
          </div>
        )}
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: "56px",
            borderRadius: "12px",
            borderColor: state.isFocused ? "#A3D139" : "#E0E0E0",
            boxShadow: "none",
            "&:hover": {
              borderColor: "#A3D139",
            },
          }),
        }}
      />
    </Grid>
    {/* Phone Input */}
    <Grid item xs={12} md={6}>
      <PhoneInput
        placeholder="Enter your phone number"
        country={"in"}
        value={formData.mobile}
        onChange={(phone, country) => {
          handleInputChange("mobile", phone);
          handleInputChange("countryCode", `+${country.dialCode}`);
        }}
        inputStyle={{
          width: "18rem",
          height: "56px",
          borderRadius: "12px",
          border: "1px solid #E0E0E0",
          
        }}
        buttonStyle={{
          borderRadius: "12px 0 0 12px",
        }}
      />
    </Grid>

    {/* Country Dropdown */}
    

  </Grid>
</Grid>           
              </Grid>
              <Grid item xs={12} sx={{display:"block", marginTop: 2}}>
                  <Button
                    type="submit"
                    fullWidth
                    disabled={loading}
                    sx={{
                      bgcolor: "blue", 
                      color: "white",
                      fontWeight: "bold",
                      py: 2,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontSize: "1rem",
                      "&:hover": { bgcolor: "#92BD33",color: "black" },
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : "Send Message"}
                  </Button>
                </Grid>
            </form>

            <Typography variant="caption" sx={{ mt: 3, textAlign: "center", color: "#636E72" }}>
              By contacting us you agree to our <b>Terms of Service</b> and <b>Privacy Policy</b>.
            </Typography>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default Contact;